import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  showEditorNotificationMessageGrowl,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {openPosterEditorSlideshowApplyPropertiesModal} from '@Modals/poster-editor-slideshow-apply-properties-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorItemStretchModal} from '@Modals/poster-editor-item-stretch-modal';
import {openPosterEditorItemFlipModal} from '@Modals/poster-editor-item-flip-modal';
import {openPosterEditorSlideshowSlidesModal} from '@Modals/poster-editor-slideshow-slides-modal';
import {openPosterEditorAlignmentModal} from '@Modals/poster-editor-item-alignment-modal';
import {openPosterEditorItemFontSizeModal} from '@Modals/poster-editor-item-font-size-modal';
import {openPosterEditorItemFontStyleModal} from '@Modals/poster-editor-item-font-style-modal';
import {openPosterEditorShadowModal} from '@Modals/poster-editor-item-shadow-modal';
import {openPosterEditorTextOutlineModal} from '@Modals/poster-editor-text-outline-modal';
import {openPosterEditorTextFillModal} from '@Modals/poster-editor-text-fill-modal';
import {openPosterEditorItemBackgroundPanel} from '@Modals/poster-editor-item-background-modal';
import {openPosterEditorTextItemFontModal} from '@Modals/poster-editor-text-item-font-modal';
import {openPosterEditorSlideshowTransitionModal} from '@Modals/poster-editor-slideshow-transition-modal';
import {isUserPremium} from '@Libraries/user.library';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {openPosterEditorSpellcheckModal} from '@Modals/poster-editor-item-spellcheck-modal';
import {openPosterEditorItemListModal} from '@Modals/poster-editor-item-list-modal';
import {useEditorPrefetchTextMobileModules} from '@Hooks/poster-editor/useEditorPrefetchTextMobileModules';
import {openPosterEditorItemLetterSpacingModal} from '@Modals/poster-editor-item-letter-spacing-modal';
import {openPosterEditorLineHeightModal} from '@Modals/poster-editor-item-line-height-modal';
import {TextSlideBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {SlideItemColorFill} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/slide-item-color-fill/slide-item-color-fill';
import {useGetKeyFromTextStylesOfSelectedTextSlideItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesOfSelectedTextSlideItem';
import {useGetKeyfromSlideshowItem} from '@Hooks/poster-editor/useGetKeyfromSlideshowItem';
import {useGetNumberOfSlidesForActiveSlideshowItem} from '@Hooks/poster-editor/useGetNumberOfSlidesForActiveSlideshowItem';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './text-slide-bar.module.scss';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';
import {noop} from '@Utils/general.util';

export function TextSlideBottomBar(): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const isBold = useGetKeyFromTextStylesOfSelectedTextSlideItem('isBold');
  const isLocked = useGetKeyfromSlideshowItem('lockMovementX');
  const numOfSlides = useGetNumberOfSlidesForActiveSlideshowItem() ?? 0;

  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  useEditorPrefetchTextMobileModules();

  if (!activeItem || !activeSlide || !activeSlide.isTextSlide() || !activeItem.isSlideshow()) {
    return null;
  }

  const onClickSpellcheck = (): void => {
    if (isUserPremium()) {
      openPosterEditorSpellcheckModal();
    } else {
      window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_SPELL_CHECK);
    }
  };

  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };
  const onClickHandler = (id: TextSlideBottomBarOptions): void => {
    switch (id) {
      case TextSlideBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case TextSlideBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case TextSlideBottomBarOptions.SLIDES:
        openPosterEditorSlideshowSlidesModal();
        break;

      case TextSlideBottomBarOptions.FONT:
        openPosterEditorTextItemFontModal();
        break;

      case TextSlideBottomBarOptions.TRANSITION:
        openPosterEditorSlideshowTransitionModal();
        break;

      case TextSlideBottomBarOptions.SPELLCHECK:
        onClickSpellcheck();
        break;

      case TextSlideBottomBarOptions.BACKGROUND:
        openPosterEditorItemBackgroundPanel();
        break;
      case TextSlideBottomBarOptions.FILL:
        openPosterEditorTextFillModal();
        break;
      case TextSlideBottomBarOptions.OUTLINE:
        if (isBold) {
          showDisabledOutlineMessageGrowl();
          return;
        }
        openPosterEditorTextOutlineModal();
        break;
      case TextSlideBottomBarOptions.SIZE:
        openPosterEditorItemFontSizeModal();
        break;
      case TextSlideBottomBarOptions.STYLE:
        openPosterEditorItemFontStyleModal();
        break;
      case TextSlideBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;

      case TextSlideBottomBarOptions.ALIGNMENT:
        openPosterEditorAlignmentModal();
        break;
      case TextSlideBottomBarOptions.LETTER_SPACING:
        openPosterEditorItemLetterSpacingModal();
        break;
      case TextSlideBottomBarOptions.LINE_HEIGHT:
        openPosterEditorLineHeightModal();
        break;
      case TextSlideBottomBarOptions.LISTS:
        openPosterEditorItemListModal();
        break;
      case TextSlideBottomBarOptions.SHADOW:
        openPosterEditorShadowModal();
        break;
      case TextSlideBottomBarOptions.FLIP:
        openPosterEditorItemFlipModal();
        break;
      case TextSlideBottomBarOptions.STRETCH:
        openPosterEditorItemStretchModal();
        break;
      case TextSlideBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case TextSlideBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case TextSlideBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case TextSlideBottomBarOptions.APPLY_TO_ALL:
        openPosterEditorSlideshowApplyPropertiesModal();
        break;
      case TextSlideBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case TextSlideBottomBarOptions.DELETE:
        deleteItemByID(activeItem.uid);
        break;
      default:
        break;
    }
  };

  const showDisabledOutlineMessageGrowl = (): void => {
    showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_disabled_text_outline_mobile_text'));
  };

  const getCustomFillContent = (): ReactElement<any> => {
    return <SlideItemColorFill />;
  };

  return (
    <BottomBar
      id="poster-editing-textslide-bottom-bar"
      items={[
        {
          id: TextSlideBottomBarOptions.EDIT,
          icon: 'icon-pencil',
          text: window.i18next.t('pmwjs_edit_item'),
          onClick: (e): void => {
            if (activeItem.isSlideshow()) {
              // @ts-ignore
              activeItem?.enterEditMode(e);
            }
          },
        },
        {
          id: TextSlideBottomBarOptions.SLIDES,
          icon: 'icon-slideshow',
          text: window.i18next.t('pmwjs_slides'),
        },
        {
          id: TextSlideBottomBarOptions.FONT,
          icon: 'icon-font',
          text: window.i18next.t('pmwjs_font'),
        },
        {
          id: TextSlideBottomBarOptions.TRANSITION,
          icon: 'icon-transitions',
          text: window.i18next.t('pmwjs_transition'),
        },
        {
          id: TextSlideBottomBarOptions.SIZE,
          icon: 'icon-text-size',
          text: window.i18next.t('pmwjs_size'),
        },
        {
          id: TextSlideBottomBarOptions.FILL,
          customContent: getCustomFillContent(),
          text: window.i18next.t('pmwjs_fill'),
        },
        {
          id: TextSlideBottomBarOptions.STYLE,
          icon: 'icon-text-style',
          text: window.i18next.t('pmwjs_style'),
        },
        {
          id: TextSlideBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: TextSlideBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: TextSlideBottomBarOptions.OUTLINE,
          icon: 'icon-outline',
          text: window.i18next.t('pmwjs_outline'),
          isDisabled: isBold,
        },
        {
          id: TextSlideBottomBarOptions.ALIGNMENT,
          icon: 'icon-align-center',
          text: window.i18next.t('pmwjs_alignment'),
        },
        {
          id: TextSlideBottomBarOptions.LISTS,
          icon: 'icon-list-ordered',
          text: window.i18next.t('pmwjs_lists'),
        },
        {
          id: TextSlideBottomBarOptions.LINE_HEIGHT,
          icon: 'icon-line-height',
          text: window.i18next.t('pmwjs_line_height'),
        },
        {
          id: TextSlideBottomBarOptions.LETTER_SPACING,
          icon: 'icon-letter-spacing',
          text: window.i18next.t('pmwjs_letter_spacing'),
        },
        {
          id: TextSlideBottomBarOptions.BACKGROUND,
          icon: 'icon-background',
          text: window.i18next.t('pmwjs_background'),
        },
        {
          id: TextSlideBottomBarOptions.SHADOW,
          icon: 'icon-shadow',
          text: window.i18next.t('pmwjs_shadow'),
        },
        {
          id: TextSlideBottomBarOptions.FLIP,
          icon: 'icon-flip-horizontal',
          text: window.i18next.t('pmwjs_flip'),
        },
        {
          id: TextSlideBottomBarOptions.STRETCH,
          icon: 'icon-fit-to-width',
          text: window.i18next.t('pmwjs_stretch'),
        },
        {
          id: TextSlideBottomBarOptions.SPELLCHECK,
          icon: 'icon-spellcheck',
          text: window.i18next.t('pmwjs_spellcheck'),
          tag: !isUserPremium() ? <Icon className="color-premium" icon="icon-crown" size={IconSize.SIZE_ICON_20} /> : undefined,
        },
        {
          id: TextSlideBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: TextSlideBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: TextSlideBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: TextSlideBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: TextSlideBottomBarOptions.APPLY_TO_ALL,
          icon: 'icon-apply-to-all-slides',
          text: window.i18next.t('pmwjs_apply_to_all_slides'),
          isHidden: numOfSlides < 2,
        },
        {
          id: TextSlideBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: TextSlideBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
      onClick={onClickHandler}
    />
  );
}
